import { ReactNode } from 'react'
import { LuBan, LuInfo } from 'react-icons/lu'
import { OptionProps } from 'react-select'

import { SelectOption } from '@/lib/core/components/Select'
import { Colors } from '@/lib/core/components/Select/Select.styles'
import {
  StyledActionOption,
  StyledOption,
} from '@/lib/core/components/Select/components/Option.styles'
import { getOptionHighlight } from '@/lib/core/components/Select/components/Option.utils'
import { Tooltip } from '@/lib/core/components/Tooltip'

import styles from './Option.module.css'

export type ActionOptionProps = {
  children: ReactNode
  onClick?: () => void
}

export const ActionOption = ({ children, onClick }: ActionOptionProps) => (
  <StyledActionOption onClick={onClick}>{children}</StyledActionOption>
)

export const Option = ({
  children,
  isDisabled,
  isFocused,
  isSelected,
  innerRef,
  innerProps,
  theme,
  data,
}: OptionProps<SelectOption>) => {
  const renderInfoTooltip = (tooltipText: string, testId: string) => (
    <div className={[styles.flexCenter, styles.rightIcon].join(' ')} data-testid={testId}>
      <Tooltip
        interactive
        trigger="mouseenter"
        arrowOffset={24}
        contentPadding={0}
        placement="right"
        tooltipContent={<div className={styles.tooltip}>{tooltipText}</div>}
      >
        <span>
          <LuInfo color="#15a1ce" size="16" strokeWidth={1.6} />
        </span>
      </Tooltip>
    </div>
  )

  const renderTooltip = () => {
    if (isDisabled) {
      // The owner of the option knows best why it is disabled; fall back to
      // inference for selects that do not spell the reason out.
      const explicitReason = data?.data?.disabledReason
      const reason =
        typeof explicitReason === 'string'
          ? explicitReason
          : data?.data?.isOwnedFromOrganizationTo || data?.data?.optionType === 'SelectContact'
            ? 'Cannot share back to owner'
            : 'Already selected'

      return renderInfoTooltip(reason, 'option-disabled-reason')
    }

    // A selectable option may still carry something the operator should know
    const notice = data?.data?.notice
    return typeof notice === 'string' ? renderInfoTooltip(notice, 'option-notice') : null
  }

  return (
    <StyledOption
      aria-disabled={isDisabled}
      // Surfaces which of the two react-select states won the row, so QA can
      // assert that only ever one row claims the selected highlight.
      data-highlight={getOptionHighlight({ isDisabled, isFocused, isSelected })}
      data-testid="select-option"
      borderColor={(theme.colors as Colors).border}
      isDisabled={isDisabled}
      isFocused={isFocused}
      isSelected={isSelected}
      minHeight={`${theme.spacing.controlHeight}px`}
      ref={innerRef}
      {...innerProps}
    >
      {isDisabled ? (
        <div className={[styles.flexCenter, styles.leftIcon].join(' ')}>
          <LuBan size="16" strokeWidth={1.5} />
        </div>
      ) : null}
      <div className={styles.optionContent}>{children}</div>
      {renderTooltip()}
    </StyledOption>
  )
}
